import { useHistory } from 'react-router-dom';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import { putBulkImport } from '@/lib/services/bulkImportServices';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useDialogManager } from '@/modules/Dialogs';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import BulkImportStatus from '../config/BulkImportStatus';
import type HeaderMappings from '../types/HeaderMappings';

type UpdateBulkImportProps = {
  bulkImportId: string;
  headerMappings?: HeaderMappings;
  status: BulkImportStatus;
};

export type UpdateBulkImport = (params: UpdateBulkImportProps) => Promise<void>;

const useUpdateBulkImport = (): UpdateBulkImport => {
  const ownerOrgId = useOwnerOrgId();
  const { closeDialog } = useDialogManager();
  const { user } = useCurrentUser();
  const history = useHistory();

  const getSuccessMessage = (status: BulkImportStatus) => {
    switch (status) {
      case BulkImportStatus.HEADERS_MAPPED:
      case BulkImportStatus.READY_TO_PROCESS:
        return 'DATA_UPLOADS_CONFIRMATION_MESSAGE';
      case BulkImportStatus.CANCELED:
        return 'DATA_UPLOADS_PROCESS_CANCELED';
      case BulkImportStatus.REVERT:
        return 'DATA_UPLOADS_PROCESS_REVERTED';
      default:
        return 'DATA_UPLOADS_CONFIRMATION_MESSAGE';
    }
  };

  const updateBulkImport = async ({
    bulkImportId,
    headerMappings,
    status,
  }: UpdateBulkImportProps) => {
    try {
      await putBulkImport({
        bulkImportId,
        ownerOrgId,
        headerMappings: headerMappings ?? ({} as HeaderMappings),
        updatedBy: {
          userId: user.userId,
          displayName: user.displayName,
        },
        data: {
          status,
        },
      });
      showSuccessMessage({
        message: translate(getSuccessMessage(status)),
      });
      history.push(AuthRoutes.COMPANY_DATA_UPLOADS);
      closeDialog();
    } catch (e) {
      showErrorMessage({
        message: translate('DATA_UPLOADS_FILE_UPLOAD_OTHER'),
      });

      throw e;
    }
  };

  return updateBulkImport;
};

export default useUpdateBulkImport;
